// BrokerCalculator · live pip / margin / P/L tool driven by the real
// price feed. Pick a symbol, enter a lot size + leverage + (optionally)
// a stop-loss / take-profit price and we compute pip value, contract
// value, required margin, potential profit and risk-to-reward.
//
// File-level disable: this calculator legitimately syncs the live bid
// into the entry input via a useEffect → setEntryStr chain. The new
// `react-hooks/set-state-in-effect` rule flags every derived-state
// effect; we accept that pattern here because the user-typed value
// must take precedence over the live price the moment they type.
// All calculations follow standard MT5 conventions:
//   pip size       · 0.0001 for 5-digit FX  · 0.01 for 3-digit (JPY)
//                  · per-instrument otherwise
//   contract size  · 100k for FX  · 1 oz for XAU/XAG  · 1 unit for shares
//   pip value      · (pip_size * lots * contract_size) in quote currency
//   margin reqd    · (lots * contract_size * price) / leverage
//
// The live bid/ask comes from useLivePrices · so when the user picks
// EURUSD, the entry/SL/TP fields default to the actual market.

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ChevronDown, ArrowRight } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import useLivePrices from "@/hooks/useLivePrices";
import SymbolIcon from "@/components/SymbolIcon";

const POPULAR = [
  { sym: "EURUSD", cat: "forex" }, { sym: "GBPUSD", cat: "forex" }, { sym: "USDJPY", cat: "forex" },
  { sym: "USDCHF", cat: "forex" }, { sym: "AUDUSD", cat: "forex" }, { sym: "USDCAD", cat: "forex" },
  { sym: "NZDUSD", cat: "forex" }, { sym: "EURJPY", cat: "forex" }, { sym: "GBPJPY", cat: "forex" },
  { sym: "XAUUSD", cat: "metals" }, { sym: "XAGUSD", cat: "metals" }, { sym: "XPTUSD", cat: "metals" },
  { sym: "USOIL",  cat: "energies" }, { sym: "UKOIL", cat: "energies" },
  { sym: "BTCUSD", cat: "crypto" }, { sym: "ETHUSD", cat: "crypto" },
  { sym: "SOLUSD", cat: "crypto" }, { sym: "XRPUSD", cat: "crypto" },
  { sym: "WS30",   cat: "indices" }, { sym: "SP500",  cat: "indices" },
  { sym: "NAS100", cat: "indices" }, { sym: "DAX40",  cat: "indices" }, { sym: "UK100", cat: "indices" },
  { sym: "JPN225", cat: "indices" },
];

const LEVERAGES = [100, 200, 300, 500, 1000];

// Contract sizes per instrument family (MT5 standard).
function contractSize(sym, cat) {
  if (cat === "forex") return 100_000;
  if (cat === "metals") {
    if (sym.startsWith("XAU")) return 100;        // 100 oz
    if (sym.startsWith("XAG")) return 5_000;      // 5,000 oz
    return 100;
  }
  if (cat === "energies") return 1_000;           // 1,000 barrels
  if (cat === "indices") return 1;
  if (cat === "crypto") return 1;
  return 1;
}

function pipSize(sym, cat) {
  if (cat === "forex") return sym.endsWith("JPY") ? 0.01 : 0.0001;
  if (cat === "metals") {
    if (sym.startsWith("XAU")) return 0.01;
    if (sym.startsWith("XAG")) return 0.001;
    return 0.01;
  }
  if (cat === "energies") return 0.01;
  if (cat === "indices") return 1;
  if (cat === "crypto") return 1;
  return 0.0001;
}

function digits(sym, cat) {
  if (cat === "forex") return sym.endsWith("JPY") ? 3 : 5;
  if (cat === "metals") return sym.startsWith("XAU") ? 2 : 3;
  if (cat === "energies") return 2;
  if (cat === "indices") return 2;
  if (cat === "crypto") return sym === "BTCUSD" || sym === "ETHUSD" ? 1 : 4;
  return 5;
}

// Symbol picker · searchable list of POPULAR symbols.
function SymbolPicker({ value, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const cur = POPULAR.find((o) => o.sym === value) || POPULAR[0];
  const list = useMemo(
    () => POPULAR.filter((o) => o.sym.toLowerCase().includes(q.trim().toLowerCase())),
    [q]
  );
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); setQ(""); }}
        className="w-full inline-flex items-center gap-2.5 h-11 px-3 rounded-xl border transition-colors hover:border-[color:var(--brand)]"
        style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)" }}
        data-testid="calc-symbol-picker"
      >
        <SymbolIcon symbol={cur.sym} category={cur.cat} size={22} />
        <span className="font-mono text-[14px] text-text-1 flex-1 text-left">{cur.sym}</span>
        <ChevronDown size={14} className="text-text-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute left-0 right-0 top-full mt-1.5 z-[61] rounded-xl border py-1 max-h-72 overflow-hidden"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)", boxShadow: "var(--shadow-md)" }}
            data-testid="calc-symbol-picker-list"
          >
            <div className="px-2 pt-1 pb-1.5 border-b" style={{ borderColor: "var(--line)" }}>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("comp.search.searchSymbolPlaceholder")}
                className="w-full h-8 px-2 rounded-md text-[13px] font-mono bg-transparent border focus:outline-none focus:border-[color:var(--brand)]"
                style={{ borderColor: "var(--line-strong)", color: "var(--text-1)" }}
              />
            </div>
            <ul className="overflow-y-auto max-h-56">
              {list.map((o) => (
                <li key={o.sym}>
                  <button
                    type="button"
                    onClick={() => { onChange(o.sym, o.cat); setOpen(false); }}
                    className={`w-full flex items-center gap-2.5 text-left px-3 py-2 text-[13px] font-mono transition-colors ${
                      o.sym === value
                        ? "text-[color:var(--brand)] bg-[color:color-mix(in_srgb,var(--brand)_10%,transparent)]"
                        : "text-text-2 hover:text-text-1 hover:bg-[color:var(--surface-2)]"
                    }`}
                    data-testid={`calc-pick-${o.sym}`}
                  >
                    <SymbolIcon symbol={o.sym} category={o.cat} size={18} />
                    {o.sym}
                  </button>
                </li>
              ))}
              {list.length === 0 && (
                <li className="px-3 py-2 text-[12px] text-text-3">No symbol matches &ldquo;{q}&rdquo;</li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function Field({ label, hint, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="font-mono text-[12px] uppercase tracking-[0.18em] text-text-3">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="text-[12px] text-text-3 mt-1 block">{hint}</span>}
    </label>
  );
}

function Stat({ label, value, accent = false, testId }) {
  return (
    <div
      className="rounded-xl border p-3.5"
      style={{
        borderColor: accent ? "color-mix(in srgb, var(--brand) 32%, transparent)" : "var(--line-strong)",
        background: accent ? "color-mix(in srgb, var(--brand) 6%, transparent)" : "var(--surface-2)",
      }}
      data-testid={testId}
    >
      <div className="font-mono text-[12px] uppercase tracking-[0.18em] text-text-3">{label}</div>
      <div className={`font-display ${accent ? "text-text-1" : "text-text-1"} text-[18px] sm:text-[22px] font-semibold tabular-nums mt-1.5 leading-none`}>
        {value}
      </div>
    </div>
  );
}

function fmtMoney(v, ccy = "USD") {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { style: "currency", currency: ccy, minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function BrokerCalculator() {
  const { t } = useTranslation();
  const { prices } = useLivePrices();
  const [sym, setSym] = useState("EURUSD");
  const [cat, setCat] = useState("forex");
  const [lots, setLots] = useState(1);
  const [leverage, setLeverage] = useState(500);
  const [side, setSide] = useState("buy");
  const [entryStr, setEntryStr] = useState("");
  const [slStr, setSlStr] = useState("");
  const [tpStr, setTpStr] = useState("");

  // Auto-fill the entry input with the live price when the user changes
  // symbol, but only when the user hasn't typed a value.
  const live = prices?.[sym];
  useEffect(() => {
    if (!live) return;
    setEntryStr((cur) => (cur === "" ? live.bid.toFixed(digits(sym, cat)) : cur));
  }, [sym, cat, live?.bid]);

  const entry = parseFloat(entryStr) || live?.bid || 0;
  const sl    = parseFloat(slStr);
  const tp    = parseFloat(tpStr);
  const cs    = contractSize(sym, cat);
  const ps    = pipSize(sym, cat);

  // pip value · for FX where quote ccy is USD, this collapses neatly.
  // For non-USD-quote pairs we'd need a cross rate; we approximate using
  // live bid (good enough for the marketing-page demo).
  const pipValueQuote = ps * lots * cs;
  const isUsdQuote = sym.endsWith("USD") || cat === "metals" || cat === "energies" || cat === "indices" || cat === "crypto";
  const pipValueUsd = isUsdQuote
    ? pipValueQuote
    : sym.endsWith("JPY")
      ? (pipValueQuote / Math.max(entry, 0.0001))
      : pipValueQuote * (prices?.[`${sym.slice(3)}USD`]?.bid || prices?.[`USD${sym.slice(3)}`]?.bid ? 1 / (prices?.[`USD${sym.slice(3)}`]?.bid || 1) : 1);

  const contractValue = lots * cs * entry;
  const marginUsd     = leverage > 0 ? contractValue / leverage : 0;

  const pl = (target) => {
    if (!Number.isFinite(target) || !Number.isFinite(entry)) return null;
    const dirSign = side === "buy" ? 1 : -1;
    const pips = ((target - entry) / ps) * dirSign;
    return pips * pipValueUsd;
  };
  const slPl = pl(sl);
  const tpPl = pl(tp);
  const rr   = (slPl !== null && tpPl !== null && slPl < 0 && tpPl > 0)
    ? Math.abs(tpPl / slPl)
    : null;

  const d = digits(sym, cat);

  return (
    <section data-testid="broker-calculator" className="py-14 lg:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15% 0px" }}
          transition={{ duration: 0.55 }}
          className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8"
        >
          {/* Inputs */}
          <div
            className="lg:col-span-2 rounded-2xl border p-6 sm:p-7"
            style={{ borderColor: "var(--line-strong)", background: "var(--surface)" }}
          >
            <div className="flex items-center gap-2 mb-5">
              <Calculator size={16} className="text-brand" />
              <h3 className="font-display text-lg font-medium text-text-1">{t("comp.calc.tradeParameters")}</h3>
            </div>

            <Field label={t("comp.calc.instrument")} testId="field-instrument">
              <SymbolPicker value={sym} onChange={(s, c) => { setSym(s); setCat(c); setEntryStr(""); setSlStr(""); setTpStr(""); }} />
            </Field>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <Field label={t("comp.calc.side")} testId="field-side">
                <div className="grid grid-cols-2 gap-1 p-1 rounded-xl border" style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)" }}>
                  <button
                    type="button"
                    onClick={() => setSide("buy")}
                    className={`h-9 rounded-lg text-[12.5px] font-medium transition-colors ${side === "buy" ? "text-[#54d97c]" : "text-text-3"}`}
                    style={side === "buy" ? { background: "color-mix(in srgb, #54d97c 12%, transparent)" } : {}}
                    data-testid="calc-side-buy"
                  >
                    {t("comp.calc.buy")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSide("sell")}
                    className={`h-9 rounded-lg text-[12.5px] font-medium transition-colors ${side === "sell" ? "text-[#f06a6a]" : "text-text-3"}`}
                    style={side === "sell" ? { background: "color-mix(in srgb, #f06a6a 12%, transparent)" } : {}}
                    data-testid="calc-side-sell"
                  >
                    {t("comp.calc.sell")}
                  </button>
                </div>
              </Field>
              <Field label={t("comp.calc.lotSize")} hint={t("comp.calc.lotHint")} testId="field-lots">
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0.01"
                  value={lots}
                  onChange={(e) => setLots(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full h-11 px-3 rounded-xl border font-mono text-[14px] tabular-nums focus:outline-none focus:border-[color:var(--brand)]"
                  style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)", color: "var(--text-1)" }}
                  data-testid="calc-lots-input"
                />
              </Field>
            </div>

            <Field label="Leverage" testId="field-leverage">
              <div className="grid grid-cols-5 gap-1 p-1 rounded-xl border" style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)" }}>
                {LEVERAGES.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLeverage(l)}
                    className={`h-9 rounded-lg text-[12px] font-mono transition-colors ${leverage === l ? "text-[color:var(--brand)]" : "text-text-3 hover:text-text-1"}`}
                    style={leverage === l ? { background: "color-mix(in srgb, var(--brand) 12%, transparent)" } : {}}
                    data-testid={`calc-lev-${l}`}
                  >
                    1:{l}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <Field label={t("comp.calc.entry")} testId="field-entry">
                <input
                  type="text"
                  inputMode="decimal"
                  value={entryStr}
                  onChange={(e) => setEntryStr(e.target.value)}
                  placeholder={live ? live.bid.toFixed(d) : "—"}
                  className="w-full h-11 px-3 rounded-xl border font-mono text-[13px] tabular-nums focus:outline-none focus:border-[color:var(--brand)]"
                  style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)", color: "var(--text-1)" }}
                  data-testid="calc-entry-input"
                />
              </Field>
              <Field label={t("comp.calc.stopLoss")} testId="field-sl">
                <input
                  type="text"
                  inputMode="decimal"
                  value={slStr}
                  onChange={(e) => setSlStr(e.target.value)}
                  placeholder={t("comp.calc.optional")}
                  className="w-full h-11 px-3 rounded-xl border font-mono text-[13px] tabular-nums focus:outline-none focus:border-[color:var(--brand)]"
                  style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)", color: "var(--text-1)" }}
                  data-testid="calc-sl-input"
                />
              </Field>
              <Field label={t("comp.calc.takeProfit")} testId="field-tp">
                <input
                  type="text"
                  inputMode="decimal"
                  value={tpStr}
                  onChange={(e) => setTpStr(e.target.value)}
                  placeholder={t("comp.calc.optional")}
                  className="w-full h-11 px-3 rounded-xl border font-mono text-[13px] tabular-nums focus:outline-none focus:border-[color:var(--brand)]"
                  style={{ borderColor: "var(--line-strong)", background: "var(--surface-2)", color: "var(--text-1)" }}
                  data-testid="calc-tp-input"
                />
              </Field>
            </div>

            {/* Quick reference · fills the leftover left-column space */}
            <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--line)" }} data-testid="calc-quickref">
              <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-text-3 mb-3">{t("comp.calc.quickReference")}</div>
              <ul className="space-y-2.5 text-[12.5px] text-text-2 leading-relaxed font-light">
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-1.5 h-1.5 rounded-full mt-1.5" style={{ background: "var(--brand)" }} />
                  <span><Trans i18nKey="comp.calc.qr1" components={{ b: <span className="text-text-1 font-mono" /> }} /></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-1.5 h-1.5 rounded-full mt-1.5" style={{ background: "var(--brand)" }} />
                  <span><Trans i18nKey="comp.calc.qr2" components={{ b: <span className="text-text-1 font-mono" /> }} /></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-1.5 h-1.5 rounded-full mt-1.5" style={{ background: "var(--brand)" }} />
                  <span>{t("comp.calc.qr3")}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="shrink-0 w-1.5 h-1.5 rounded-full mt-1.5" style={{ background: "var(--brand)" }} />
                  <span><Trans i18nKey="comp.calc.qr4" components={{ b: <span className="text-text-1" /> }} /></span>
                </li>
              </ul>
            </div>
          </div>

          {/* Outputs */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Stat label={t("comp.calc.statBid")} value={live ? live.bid.toFixed(d) : "—"} testId="stat-bid" />
            <Stat label={t("comp.calc.statAsk")} value={live ? live.ask.toFixed(d) : "—"} testId="stat-ask" />
            <Stat label={t("comp.calc.statContract")} value={fmtMoney(contractValue)} testId="stat-contract" />
            <Stat label={t("comp.calc.statMargin")} value={fmtMoney(marginUsd)} accent testId="stat-margin" />
            <Stat label={t("comp.calc.statPip")} value={fmtMoney(pipValueUsd)} testId="stat-pip" />
            <Stat label={t("comp.calc.statRR")} value={rr ? `1 : ${rr.toFixed(2)}` : "—"} testId="stat-rr" />
            <Stat
              label={t("comp.calc.statSL")}
              value={slPl !== null ? fmtMoney(slPl) : "—"}
              testId="stat-sl-pl"
            />
            <Stat
              label={t("comp.calc.statTP")}
              value={tpPl !== null ? fmtMoney(tpPl) : "—"}
              accent={tpPl !== null && tpPl > 0}
              testId="stat-tp-pl"
            />

            <a
              href="https://live.ofinancial.markets/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="sm:col-span-2 btn-solid-brand h-12 justify-center mt-1"
              data-testid="calc-cta"
            >
              {t("comp.calc.ctaOpenTrade")} <ArrowRight size={15} />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
